// FAQ.js — targets the actual questions people search alongside "data
// quality", "data migration", and "data analysis" software, and doubles as
// on-page content for those terms. Mirrored 1:1 in public/index.html's
// FAQPage JSON-LD, so keep the two in sync if this copy changes.
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

export const FAQS = [
  {
    q: "What's the difference between PrismDQ and a data migration tool like Fivetran?",
    a: "Tools like Fivetran move data from A to B. PrismDQ profiles and scores it first, applies cleansing and validation rules, runs a full source-to-target migration with a quality gate at every stage, and keeps monitoring it afterwards — data quality, migration, and ongoing analysis in one platform instead of three.",
  },
  {
    q: "What does \"exposing data to AI agents\" actually mean?",
    a: "PrismDQ runs an MCP (Model Context Protocol) server alongside the app. Any MCP-compatible AI agent — Claude Desktop, Claude Code, or another MCP client — can query your entities, quality issues, rules, and run history using a personal access token, with the same permissions and audit trail as a signed-in user.",
  },
  {
    q: "Does PrismDQ just clean data once, or does it analyse it over time?",
    a: "Both. A one-off scan (Stage 1) gives you a snapshot. From Stage 2 up, PrismDQ tracks quality and coverage scores over time, so you can see trends by dataset or business area, not just a single point-in-time score.",
  },
  {
    q: "Which systems does PrismDQ connect to?",
    a: "SQL Server, Oracle, SharePoint, and Dynamics 365 Finance & Operations out of the box, either as a source or a target. Set a connection up once and reuse it across every project.",
  },
  {
    q: "Is my data used to train AI models?",
    a: "No. AI features (the in-app assistant and AI-powered cleansing suggestions) only see the data needed to answer a given question — never a bulk export — and none of it is used to train public models.",
  },
  {
    q: "Do I have to commit to a subscription before I know if my data has a problem?",
    a: "No. Start with a one-off £49 scan on a single dataset — no subscription. If it turns up something worth fixing, move up to Assurance or Programme; if not, you keep the report and walk away.",
  },
  {
    q: "Can I self-host PrismDQ?",
    a: "Yes, at the Enterprise Protection tier — host it yourself on your own Azure environment, or let us host it for you.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" className="relative py-20">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            Frequently asked questions
          </h2>
        </div>

        <div className="mt-10 space-y-3">
          {FAQS.map((item, i) => {
            const isOpen = open === i;
            return (
              <div
                key={item.q}
                className="overflow-hidden rounded-2xl border border-blue-100 bg-white/70 backdrop-blur-md"
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="text-sm font-semibold text-slate-900 sm:text-base">
                    {item.q}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 text-blue-600 transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="px-5"
                    >
                      <p className="pb-4 text-sm leading-relaxed text-slate-600">
                        {item.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
